import Blank from '@doltech/ui/lib/figma/BlankEditor/Result/Blank';
import { QuestionDisplayMode } from '@doltech/ui/lib/figma/CircleText/CircleText';
import { CollapseExpandPanel } from '@doltech/ui/lib/figma/CollapseExpandPanel/CollapseExpandPanel';
import { figmaColors } from '@doltech/ui/lib/figma/colors';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Box } from '@doltech/ui/lib/figma/Common/Box';
import { QuestionV2 } from '@doltech/ui/lib/figma/Question/QuestionV2';
import { TestModeEnum } from '@doltech/utils/lib/constants';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { FontSizeElementVariants } from '@doltech/ui/lib/figma/DolEditor/decorators/font-size-switcher/components/FontSizeSwitcherElement.types';
import { DoTestEditor } from '@doltech/ui/lib/figma/DoTestEditor/DoTestEditor';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { isJson } from '@doltech/utils/lib/object';
import { ResultShortQuestionInstance } from '../../model/Question/result-short-question.store';
import { ExtraQuestionProps } from './BaseQuestion';

export const Main = styled(Box)``;

const QuestionLists = styled.div`
  display: grid;
  gap: 12px;
`;

export const Container = styled.div<{ hasExpand?: boolean }>`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${figmaColors.text1};
  align-items: center;
  display: flex;
  flex-wrap: wrap;

  .circle-text {
    display: inline-flex;
    margin-right: 12px;
  }

  .question-do-test {
    &.${QuestionDisplayMode.QUESTION_OPTION_CORRECT} {
      background-color: ${colorsV2.green100};
    }
    &.${QuestionDisplayMode.QUESTION_OPTION_WRONG} {
      background-color: ${colorsV2.red100};
    }
    &.${QuestionDisplayMode.QUESTION_OPTION_MISSING} {
      background-color: ${colorsV2.black5};
      > span {
        color: ${colorsV2.black100};
      }
    }
  }
`;

const BlankWrapper = styled.span`
  margin-left: 8px;
  margin-right: 8px;
`;

const TextWrapper = styled.main`
  line-height: 24px;
  color: ${colorsV2.black100};
  font-weight: 400;
  display: flex;
  align-items: baseline;
`;

interface ShortAnswerProps {
  data: ResultShortQuestionInstance & ExtraQuestionProps;
  testMode: TestModeEnum;
}

const ShortAnswerResult = (props: ShortAnswerProps) => {
  const { data, testMode } = props;
  const { sentences, blankNo, userAnswers, marked, correctAnswers, index } = data;
  const [expand, setExpand] = React.useState(false);

  const getUserAnswer = React.useCallback(
    (id) => {
      return get(
        userAnswers.find((it) => it.blankId === id),
        'userAnswer'
      );
    },
    [userAnswers]
  );

  const getCorrectAnswer = React.useCallback(
    (id) => {
      return get(
        correctAnswers.find((it) => it.key === id),
        'value'
      );
    },
    [correctAnswers]
  );

  const questionCollapsed = () => {
    const sentence = sentences.find((it) => it.key === index);
    return (
      <Container>
        <TextWrapper>
          <QuestionV2 no={blankNo + Number(sentence.key)} className="circle-text" />
          <ResponsiveTypography.Paragraph variant="regular/14-20" color="black100">
            <DoTestEditor
              initialValue={
                isJson(sentence.text)
                  ? JSON.parse(sentence.text)
                  : [
                      {
                        type: FontSizeElementVariants.PARAGRAPH,
                        children: [
                          {
                            text: sentence.text ?? '',
                          },
                        ],
                      },
                    ]
              }
            />
            <BlankWrapper>
              <Blank
                blank={{
                  key: Number(sentence.key),
                  no: blankNo + Number(sentence.key),
                  id: sentence.key.toString(),
                  userAnswer: getUserAnswer(sentence.key.toString()),
                  correctAnswer: getCorrectAnswer(sentence.key),
                  markedType: marked[sentence.key],
                }}
                testMode={testMode}
                showQuestionNo={false}
              />
            </BlankWrapper>
          </ResponsiveTypography.Paragraph>
        </TextWrapper>
      </Container>
    );
  };

  const questionExpanded = () => {
    return (
      <QuestionLists className="question-lists">
        {sentences.map((sentence) => {
          return (
            <Container hasExpand key={sentence.key}>
              <TextWrapper>
                <QuestionV2 no={blankNo + Number(sentence.key)} className="circle-text" />
                <ResponsiveTypography.Paragraph variant="regular/14-20" color="black100">
                  <DoTestEditor
                    initialValue={
                      isJson(sentence.text)
                        ? JSON.parse(sentence.text)
                        : [
                            {
                              type: FontSizeElementVariants.PARAGRAPH,
                              children: [
                                {
                                  text: sentence.text ?? '',
                                },
                              ],
                            },
                          ]
                    }
                  />
                  <BlankWrapper>
                    <Blank
                      blank={{
                        key: Number(sentence.key),
                        no: blankNo + Number(sentence.key),
                        id: sentence.key.toString(),
                        userAnswer: getUserAnswer(sentence.key.toString()),
                        correctAnswer: getCorrectAnswer(sentence.key),
                        markedType: marked[sentence.key],
                      }}
                      showQuestionNo={false}
                    />
                  </BlankWrapper>
                </ResponsiveTypography.Paragraph>
              </TextWrapper>
            </Container>
          );
        })}
      </QuestionLists>
    );
  };

  return (
    <Main
      as={CollapseExpandPanel}
      key={blankNo + index}
      className="dol-question short-answer"
      onToggleCollapse={() => setExpand((oldExpandValue) => !oldExpandValue)}
      expand={expand}
      collapsedSection={questionCollapsed()}
      expandedSection={questionExpanded()}
    />
  );
};

export default observer(ShortAnswerResult);
